import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Category, categoryService } from '../../services/categoryService';
import { Tour } from '../../types';
import { tourService } from '../../services/tourService';
import { useTheme } from '../../contexts/ThemeContext';
import CircularCardWheel from './CircularCardWheel';
import SpinResult from './SpinResult';
import CelebrationParticles from './CelebrationParticles';

interface SpinWheelModalProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'categories' | 'tours';
}

const SpinWheelModal: React.FC<SpinWheelModalProps> = ({ isOpen, onClose, mode }) => {
    const { actualTheme } = useTheme();
    const [items, setItems] = useState<(Category | Tour)[]>([]);
    const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [isSpinning, setIsSpinning] = useState(false);
    const [result, setResult] = useState<Category | Tour | null>(null);
    const [showResult, setShowResult] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadItems();
        }
    }, [isOpen, mode]);

    const loadItems = async () => {
        setLoading(true);
        try {
            if (mode === 'categories') {
                const categories = await categoryService.getAllCategories();
                setItems(categories);

                // Fetch images for categories
                const images: Record<string, string> = {};
                const allTours = await tourService.getAllTours(false);

                for (const category of categories) {
                    // Find first tour with this category
                    const categoryTour = allTours.find(t =>
                        t.category?.toLowerCase() === category.name.toLowerCase() &&
                        t.images && t.images.length > 0
                    );

                    if (categoryTour && categoryTour.images?.length > 0) {
                        images[category.id] = categoryTour.images[0];
                    }
                }
                setCategoryImages(images);
            } else {
                const tours = await tourService.getAllTours(false);
                setItems(tours.filter(t => t.active));
            }
        } catch (error) {
            console.error('Error loading items:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSpin = () => {
        if (isSpinning || items.length === 0) return;
        setIsSpinning(true);
        setShowResult(false);
        setResult(null);
    };

    const handleSpinComplete = () => {
        setIsSpinning(false);

        // Show result after a short delay
        setTimeout(() => {
            setShowResult(true);
        }, 500);
    };

    const handleResult = (item: Category | Tour) => {
        setResult(item);
    };

    const handleSpinAgain = () => {
        setShowResult(false);
        setResult(null);
        handleSpin();
    };

    const handleClose = () => {
        setShowResult(false);
        setResult(null);
        setIsSpinning(false);
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-xl"
                        onClick={handleClose}
                    />

                    {/* Modal content - Translucent Circular Window (Enforced Light Mode) */}
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
                        animate={{ 
                            opacity: showResult ? 0 : 1, 
                            scale: showResult ? 0.8 : 1,
                            rotate: showResult ? -10 : 0
                        }}
                        transition={{ type: 'spring', damping: 20 }}
                        className={`relative z-10 w-[90vmin] h-[90vmin] max-w-[800px] max-h-[800px] rounded-full overflow-hidden backdrop-blur-2xl border border-white/40 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] bg-white/40 text-slate-800 ${showResult ? 'pointer-events-none' : ''}`}
                    >
                        {/* Header Just Close Button */}
                        <button
                            onClick={handleClose}
                            className="absolute top-[15%] right-[15%] z-50 p-4 rounded-full transition-all hover:scale-110 text-slate-800 bg-white/60 hover:bg-white shadow-lg border border-white/20"
                            style={{ transform: 'translate(50%, -50%)' }}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Wheel Content - Pinned to modal boundaries for perfect centering */}
                        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center">
                                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mb-6"></div>
                                    <p className="text-slate-800 font-bold uppercase tracking-widest text-[10px]">
                                        Mapping {mode}...
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <CircularCardWheel
                                        items={items}
                                        mode={mode}
                                        categoryImages={categoryImages}
                                        onResult={handleResult}
                                        isSpinning={isSpinning}
                                        onSpinComplete={handleSpinComplete}
                                        onStartSpin={handleSpin}
                                    />

                                    {/* Status Text */}
                                    <div className="absolute bottom-12 left-0 right-0 text-center pointer-events-none">
                                        {!isSpinning && items.length > 0 && (
                                            <p className="text-[10px] font-black tracking-[0.3em] uppercase opacity-40 text-slate-900">
                                                {items.length} {mode} in orbit
                                            </p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>

                    {/* SEPARATE CONTAINER FOR RESULT - Floating and unconstrained */}
                    <AnimatePresence>
                        {showResult && result && (
                            <motion.div 
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none"
                            >
                                <CelebrationParticles />
                                <div className="relative max-w-sm w-full pointer-events-auto">
                                    {/* Close button for result view */}
                                    <button
                                        onClick={handleClose}
                                        className="absolute -top-16 right-0 z-50 p-3 rounded-full bg-black/20 hover:bg-black/40 text-white backdrop-blur-xl border border-white/10 transition-all hover:scale-110 shadow-2xl"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>

                                    <SpinResult
                                        item={result}
                                        mode={mode}
                                        categoryImages={categoryImages}
                                        onSpinAgain={handleSpinAgain}
                                        onClose={handleClose}
                                    />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            )}
        </AnimatePresence>
    );
};

export default SpinWheelModal;
// cache bust
